import { palette5 } from '../utils/colorPalette';
import { HAIR_STYLES } from '../data/hairStyles';
import { CHARACTER_BASE_B64, CHARACTER_HAIR_B64 } from '../data/characterBaseB64';
import type { Outfit, OutfitSlot, HairStyle, Rarity, IconStyle } from '../types';

// 합성에 필요한 부분 정보 (Outfit의 부분집합)
export type EquippedPart = {
  id: string;
  color: string;
  hairStyle?: HairStyle;
  rarity?: Rarity;
  iconStyle?: IconStyle;
};

export type EquippedMap = Partial<Record<OutfitSlot, EquippedPart>>;

const SKIN = palette5('#ffd6b8');
const SKIN_CHEEK = '#ff9bb3';

export type CharacterExpression = 'normal' | 'happy' | 'wink' | 'surprised' | 'blush';

export const OutfitRenderer = {
  /**
   * Outfit 객체에서 합성용 EquippedPart 만들기
   */
  toEquipped(o: Outfit): EquippedPart {
    return { id: o.id, color: o.color, hairStyle: o.hairStyle, rarity: o.rarity, iconStyle: o.iconStyle };
  },

  /**
   * 캐릭터 + 의상 + 헤어 + 펫 안 + 안경 등 → 전체 SVG 문자열
   * @param frame 0=idle, 1=step-left, 2=step-right (walk cycle frames)
   * @param expression 표정 (normal/happy/wink/surprised/blush)
   */
  renderCharacterSVG(eq: EquippedMap, frame: 0 | 1 | 2 = 0, expression: CharacterExpression = 'normal'): string {
    const top = eq.top ? palette5(eq.top.color) : palette5('#ffffff');
    const bottom = eq.bottom ? palette5(eq.bottom.color) : palette5('#ffffff');
    const hairColor = eq.hair?.color ?? '#3a2418';
    const hairStyle: HairStyle = eq.hair?.hairStyle ?? 'buns';
    const hair = HAIR_STYLES[hairStyle](hairColor);
    const shoes = eq.shoes ? palette5(eq.shoes.color) : palette5('#3a3a3a');
    const hairpin = eq.hairpin;
    const glasses = eq.glasses;
    const bag = eq.bag;
    const isLegend = Object.values(eq).some(o => o?.rarity === 'legend');
    const hairPal = palette5(hairColor);

    // Walk cycle leg/arm offsets
    // frame 0 = idle (0,0), frame 1 = left leg forward, frame 2 = right leg forward
    const legOffsets: [number, number] = frame === 1 ? [-4, 4] : frame === 2 ? [4, -4] : [0, 0];
    const armOffsets: [number, number] = frame === 1 ? [3, -3] : frame === 2 ? [-3, 3] : [0, 0];

    // 헤어 스타일에 AI PNG 변형이 있으면 그걸 베이스로 사용 (헤어가 이미 그려져 있음)
    // 없으면 대머리 베이스 + SVG 헤어 합성
    const hairKey = eq.hair?.hairStyle ?? 'buns';
    const aiHairPng = CHARACTER_HAIR_B64[hairKey];
    const useAIHair = !!aiHairPng;
    const baseImageB64 = useAIHair ? aiHairPng : CHARACTER_BASE_B64;

    void hairPal; void armOffsets; void legOffsets; void expression;
    return `
<svg viewBox="0 0 192 288" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" shape-rendering="crispEdges">
  ${isLegend ? `
  <circle cx="28" cy="120" r="4" fill="#fff8d8" opacity="0.9">
    <animate attributeName="opacity" values="0;0.9;0" dur="2s" repeatCount="indefinite"/>
  </circle>
  <circle cx="164" cy="160" r="4" fill="#ffd95c" opacity="0.9">
    <animate attributeName="opacity" values="0;0.9;0" dur="2.4s" begin="0.5s" repeatCount="indefinite"/>
  </circle>
  ` : ''}
  <!-- 1. 가방 (캐릭터 뒤) -->
  <g transform="scale(2) translate(0, 5)">${bag ? this.renderBag(bag.color) : ''}</g>
  ${!useAIHair ? `
  <!-- 2. SVG 헤어 뒷머리 (AI 변형 없을 때만) -->
  ${hair.back}
  ` : ''}
  <!-- 3. AI 캐릭터 베이스 — 헤어 변형 PNG 또는 대머리 PNG -->
  <image href="${baseImageB64}" x="0" y="0" width="192" height="288" preserveAspectRatio="xMidYMid meet"/>
  <!-- 4. 의상 오버레이 -->
  ${top ? this._renderTopOverlay(top) : ''}
  ${bottom ? this._renderBottomOverlay(bottom) : ''}
  ${shoes ? this._renderShoesOverlay(shoes) : ''}
  ${!useAIHair ? `
  <!-- 5. SVG 헤어 앞머리 (AI 변형 없을 때만) -->
  ${hair.front}
  ` : ''}
  <!-- 6. 머리띠/안경 -->
  <g transform="scale(2)">
    ${hairpin ? this.renderHairpin(hairpin.color, hairpin.iconStyle) : ''}
    ${glasses ? this.renderGlasses(glasses.color) : ''}
  </g>
</svg>`;
  },

  /** AI 캐릭터 탱크탑 위에 색상/디자인 오버레이 — 흰 탱크를 새 색으로 덮음 */
  _renderTopOverlay(top: ReturnType<typeof palette5>): string {
    // AI 캐릭터 탱크탑 영역 추정: x=66-126, y=128-198 (192×288 grid)
    return `
      <!-- 어깨/넥라인 (밝은 라인) -->
      <rect x="68" y="128" width="56" height="3" fill="${top.deep}"/>
      <!-- 본체 -->
      <rect x="64" y="131" width="64" height="68" fill="${top.base}"/>
      <!-- 양쪽 음영 -->
      <rect x="64" y="131" width="4" height="68" fill="${top.shadow}"/>
      <rect x="124" y="131" width="4" height="68" fill="${top.shadow}"/>
      <rect x="64" y="131" width="4" height="10" fill="${top.deep}"/>
      <rect x="124" y="131" width="4" height="10" fill="${top.deep}"/>
      <!-- 위쪽 하이라이트 -->
      <rect x="68" y="131" width="56" height="3" fill="${top.light}"/>
      <!-- 가운데 세로 하이라이트 -->
      <rect x="78" y="134" width="3" height="60" fill="${top.light}" opacity="0.45"/>
      <!-- 단추 (장식) -->
      <rect x="93" y="138" width="3" height="3" fill="${top.deep}"/>
      <rect x="93" y="150" width="3" height="3" fill="${top.deep}"/>
      <rect x="93" y="162" width="3" height="3" fill="${top.deep}"/>
      <rect x="93" y="174" width="3" height="3" fill="${top.deep}"/>
      <!-- 밑단 어두운 라인 -->
      <rect x="64" y="195" width="64" height="4" fill="${top.deep}"/>
    `;
  },

  /** 반바지/치마 오버레이 */
  _renderBottomOverlay(bottom: ReturnType<typeof palette5>): string {
    // AI 반바지 영역: y=199-228, x=68-124
    return `
      <rect x="64" y="198" width="64" height="3" fill="${bottom.deep}"/>
      <rect x="64" y="201" width="64" height="32" fill="${bottom.base}"/>
      <rect x="64" y="201" width="64" height="2" fill="${bottom.light}"/>
      <rect x="64" y="201" width="4" height="32" fill="${bottom.shadow}"/>
      <rect x="124" y="201" width="4" height="32" fill="${bottom.shadow}"/>
      <!-- 가랑이 음영 -->
      <rect x="92" y="201" width="6" height="32" fill="${bottom.deep}"/>
      <rect x="93" y="201" width="3" height="32" fill="${bottom.shadow}"/>
    `;
  },

  /** 신발 오버레이 — AI 발 위에 그림 */
  _renderShoesOverlay(shoes: ReturnType<typeof palette5>): string {
    // AI 발 영역: y=258-280, 왼발 x=68-92, 오른발 x=100-124
    return `
      <!-- 왼발 -->
      <rect x="60" y="258" width="32" height="20" fill="${shoes.shadow}"/>
      <rect x="60" y="258" width="32" height="14" fill="${shoes.base}"/>
      <rect x="60" y="258" width="32" height="3" fill="${shoes.light}"/>
      <rect x="60" y="272" width="32" height="6" fill="${shoes.deep}"/>
      <rect x="64" y="262" width="6" height="2" fill="${shoes.rim}" opacity="0.7"/>
      <!-- 오른발 -->
      <rect x="100" y="258" width="32" height="20" fill="${shoes.shadow}"/>
      <rect x="100" y="258" width="32" height="14" fill="${shoes.base}"/>
      <rect x="100" y="258" width="32" height="3" fill="${shoes.light}"/>
      <rect x="100" y="272" width="32" height="6" fill="${shoes.deep}"/>
      <rect x="104" y="262" width="6" height="2" fill="${shoes.rim}" opacity="0.7"/>
    `;
  },

  // ─── HD (192×288) 얼굴/눈/볼/입 ─────────────────────────────────────────

  /** HD 얼굴 — AI 레퍼런스 풍의 둥근 chibi 얼굴 + 음영 */
  renderFaceHD(hairPal: ReturnType<typeof palette5>): string {
    return `
      <!-- 얼굴 외곽 그림자 -->
      <rect x="56" y="78" width="80" height="4" fill="${SKIN.deep}"/>
      <rect x="54" y="80" width="84" height="2" fill="${SKIN.shadow}"/>
      <!-- 얼굴 본체 (둥근 모양) -->
      <rect x="52" y="82" width="88" height="48" fill="${SKIN.base}"/>
      <!-- 양쪽 가장자리 음영 -->
      <rect x="52" y="82" width="3" height="46" fill="${SKIN.shadow}"/>
      <rect x="137" y="82" width="3" height="46" fill="${SKIN.shadow}"/>
      <rect x="52" y="82" width="3" height="6" fill="${SKIN.deep}"/>
      <rect x="137" y="82" width="3" height="6" fill="${SKIN.deep}"/>
      <!-- 얼굴 위쪽 라이트 (이마 하이라이트) -->
      <rect x="60" y="82" width="72" height="2" fill="${SKIN.light}"/>
      <rect x="74" y="82" width="44" height="3" fill="${SKIN.rim}"/>
      <!-- 턱 그림자 -->
      <rect x="56" y="124" width="80" height="4" fill="${SKIN.shadow}"/>
      <rect x="64" y="128" width="64" height="3" fill="${SKIN.shadow}"/>
      <rect x="68" y="130" width="56" height="2" fill="${SKIN.deep}"/>
      <!-- 이마 위 헤어라인 그림자 (헤어 베이스컬러로 살짝) -->
      <rect x="64" y="86" width="12" height="2" fill="${hairPal.deep}" opacity="0.3"/>
      <rect x="116" y="86" width="12" height="2" fill="${hairPal.deep}" opacity="0.3"/>
    `;
  },

  /** HD 눈 — 큰 anime 눈 + 다층 하이라이트 */
  renderEyesHD(hairPal: ReturnType<typeof palette5>, expression: CharacterExpression = 'normal'): string {
    // 표정별 눈 디자인
    if (expression === 'happy') {
      // ㅂ자 눈 (활짝 웃음)
      return `
        <rect x="62" y="106" width="22" height="3" fill="${hairPal.deep}"/>
        <rect x="64" y="103" width="18" height="3" fill="${hairPal.deep}"/>
        <rect x="66" y="100" width="14" height="3" fill="${hairPal.deep}"/>
        <rect x="108" y="106" width="22" height="3" fill="${hairPal.deep}"/>
        <rect x="110" y="103" width="18" height="3" fill="${hairPal.deep}"/>
        <rect x="112" y="100" width="14" height="3" fill="${hairPal.deep}"/>
      `;
    }
    if (expression === 'wink') {
      return `
        <!-- 왼눈 정상 -->
        <rect x="60" y="92" width="24" height="20" fill="#ffffff"/>
        <rect x="62" y="92" width="20" height="20" fill="#ffffff"/>
        <rect x="64" y="94" width="16" height="16" fill="${hairPal.base}"/>
        <rect x="66" y="96" width="12" height="12" fill="${hairPal.deep}"/>
        <rect x="68" y="98" width="8" height="8" fill="#000"/>
        <rect x="70" y="98" width="3" height="3" fill="#fff"/>
        <rect x="74" y="104" width="2" height="2" fill="#fff"/>
        <rect x="60" y="90" width="24" height="2" fill="${hairPal.deep}"/>
        <!-- 오른눈 윙크 -->
        <rect x="108" y="104" width="22" height="3" fill="${hairPal.deep}"/>
        <rect x="110" y="101" width="18" height="3" fill="${hairPal.deep}"/>
        <rect x="108" y="90" width="22" height="2" fill="${hairPal.deep}"/>
      `;
    }
    if (expression === 'surprised') {
      return `
        <rect x="58" y="88" width="28" height="28" fill="#ffffff"/>
        <rect x="60" y="90" width="24" height="24" fill="${hairPal.base}"/>
        <rect x="62" y="92" width="20" height="20" fill="${hairPal.deep}"/>
        <rect x="64" y="94" width="16" height="16" fill="#000"/>
        <rect x="66" y="96" width="4" height="4" fill="#fff"/>
        <rect x="58" y="86" width="28" height="2" fill="${hairPal.deep}"/>
        <rect x="106" y="88" width="28" height="28" fill="#ffffff"/>
        <rect x="108" y="90" width="24" height="24" fill="${hairPal.base}"/>
        <rect x="110" y="92" width="20" height="20" fill="${hairPal.deep}"/>
        <rect x="112" y="94" width="16" height="16" fill="#000"/>
        <rect x="114" y="96" width="4" height="4" fill="#fff"/>
        <rect x="106" y="86" width="28" height="2" fill="${hairPal.deep}"/>
      `;
    }
    // normal/blush — 큰 anime 눈
    return `
      <!-- 왼쪽 눈 -->
      <!-- 흰자 -->
      <rect x="60" y="90" width="24" height="2" fill="${hairPal.deep}"/>
      <rect x="60" y="92" width="24" height="20" fill="#ffffff"/>
      <!-- 홍채 외곽 (어두운) -->
      <rect x="62" y="92" width="20" height="20" fill="${hairPal.shadow}"/>
      <!-- 홍채 본체 -->
      <rect x="64" y="94" width="16" height="16" fill="${hairPal.base}"/>
      <!-- 동공 (어두운 중심) -->
      <rect x="66" y="96" width="12" height="12" fill="${hairPal.deep}"/>
      <rect x="68" y="98" width="8" height="8" fill="#000"/>
      <!-- 큰 하이라이트 -->
      <rect x="68" y="96" width="4" height="4" fill="#ffffff"/>
      <rect x="69" y="97" width="3" height="2" fill="#ffffff"/>
      <!-- 작은 하이라이트 -->
      <rect x="74" y="104" width="3" height="3" fill="#ffffff"/>
      <!-- 위 속눈썹 -->
      <rect x="60" y="89" width="24" height="2" fill="${hairPal.deep}"/>
      <rect x="58" y="91" width="2" height="3" fill="${hairPal.deep}"/>
      <rect x="84" y="91" width="2" height="3" fill="${hairPal.deep}"/>

      <!-- 오른쪽 눈 -->
      <rect x="108" y="90" width="24" height="2" fill="${hairPal.deep}"/>
      <rect x="108" y="92" width="24" height="20" fill="#ffffff"/>
      <rect x="110" y="92" width="20" height="20" fill="${hairPal.shadow}"/>
      <rect x="112" y="94" width="16" height="16" fill="${hairPal.base}"/>
      <rect x="114" y="96" width="12" height="12" fill="${hairPal.deep}"/>
      <rect x="116" y="98" width="8" height="8" fill="#000"/>
      <rect x="116" y="96" width="4" height="4" fill="#ffffff"/>
      <rect x="117" y="97" width="3" height="2" fill="#ffffff"/>
      <rect x="122" y="104" width="3" height="3" fill="#ffffff"/>
      <rect x="108" y="89" width="24" height="2" fill="${hairPal.deep}"/>
      <rect x="106" y="91" width="2" height="3" fill="${hairPal.deep}"/>
      <rect x="132" y="91" width="2" height="3" fill="${hairPal.deep}"/>
    `;
  },

  /** HD 볼터치 */
  renderCheeksHD(expression: CharacterExpression = 'normal'): string {
    const strong = expression === 'blush' || expression === 'happy';
    const op1 = strong ? '0.85' : '0.6';
    const op2 = strong ? '0.7' : '0.45';
    return `
      <rect x="56" y="114" width="10" height="4" fill="${SKIN_CHEEK}" opacity="${op1}"/>
      <rect x="58" y="118" width="6" height="2" fill="${SKIN_CHEEK}" opacity="${op2}"/>
      <rect x="54" y="116" width="14" height="2" fill="${SKIN_CHEEK}" opacity="${op2}"/>
      <rect x="126" y="114" width="10" height="4" fill="${SKIN_CHEEK}" opacity="${op1}"/>
      <rect x="128" y="118" width="6" height="2" fill="${SKIN_CHEEK}" opacity="${op2}"/>
      <rect x="124" y="116" width="14" height="2" fill="${SKIN_CHEEK}" opacity="${op2}"/>
      ${strong ? `
      <rect x="50" y="112" width="14" height="8" fill="${SKIN_CHEEK}" opacity="0.4"/>
      <rect x="128" y="112" width="14" height="8" fill="${SKIN_CHEEK}" opacity="0.4"/>
      ` : ''}
    `;
  },

  /** HD 입 — 작고 귀여운 미소 */
  renderMouthHD(expression: CharacterExpression = 'normal'): string {
    if (expression === 'happy') {
      return `
        <rect x="84" y="120" width="24" height="2" fill="#c14878"/>
        <rect x="82" y="122" width="28" height="6" fill="#e8568a"/>
        <rect x="84" y="122" width="24" height="2" fill="#ff7fa8"/>
        <rect x="84" y="124" width="6" height="3" fill="#ffffff" opacity="0.5"/>
        <rect x="86" y="126" width="20" height="2" fill="#c14878" opacity="0.7"/>
      `;
    }
    if (expression === 'surprised') {
      return `
        <rect x="88" y="118" width="16" height="2" fill="#c14878"/>
        <rect x="86" y="120" width="20" height="10" fill="#1a0010"/>
        <rect x="86" y="120" width="20" height="2" fill="#c14878"/>
        <rect x="88" y="122" width="16" height="6" fill="#3a0a20"/>
      `;
    }
    if (expression === 'wink') {
      return `
        <rect x="86" y="120" width="20" height="2" fill="#c14878"/>
        <rect x="84" y="122" width="24" height="4" fill="#e8568a"/>
        <rect x="86" y="122" width="20" height="2" fill="#ff7fa8"/>
        <rect x="90" y="124" width="4" height="2" fill="#ffffff" opacity="0.5"/>
        <rect x="106" y="124" width="4" height="3" fill="#c14878"/>
      `;
    }
    // normal/blush — 작은 미소
    return `
      <rect x="88" y="120" width="16" height="2" fill="#c14878"/>
      <rect x="88" y="122" width="16" height="3" fill="#e8568a"/>
      <rect x="90" y="122" width="12" height="1" fill="#ff7fa8"/>
      <rect x="92" y="124" width="3" height="2" fill="#ffffff" opacity="0.5"/>
    `;
  },

  renderBag(color: string): string {
    const c = palette5(color);
    return `
      <rect x="6" y="64" width="8" height="3" fill="${c.deep}"/>
      <rect x="4" y="67" width="14" height="22" fill="${c.shadow}"/>
      <rect x="4" y="67" width="14" height="20" fill="${c.base}"/>
      <rect x="4" y="67" width="14" height="2" fill="${c.light}"/>
      <rect x="14" y="69" width="2" height="16" fill="${c.deep}"/>
      <rect x="6" y="76" width="6" height="2" fill="${c.rim}"/>
    `;
  },

  renderFace(hairPal: ReturnType<typeof palette5>): string {
    return `
      <rect x="28" y="40" width="40" height="2" fill="${SKIN.shadow}"/>
      <rect x="26" y="42" width="44" height="22" fill="${SKIN.base}"/>
      <rect x="26" y="42" width="2" height="22" fill="${SKIN.shadow}"/>
      <rect x="68" y="42" width="2" height="22" fill="${SKIN.shadow}"/>
      <rect x="26" y="42" width="2" height="3" fill="${SKIN.deep}"/>
      <rect x="68" y="42" width="2" height="3" fill="${SKIN.deep}"/>
      <rect x="28" y="62" width="40" height="2" fill="${SKIN.shadow}"/>
      <rect x="32" y="64" width="32" height="2" fill="${SKIN.base}"/>
      <rect x="32" y="64" width="32" height="1" fill="${SKIN.shadow}"/>
      <rect x="32" y="65" width="32" height="1" fill="${SKIN.deep}"/>
      <rect x="32" y="44" width="6" height="1" fill="${hairPal.deep}"/>
      <rect x="58" y="44" width="6" height="1" fill="${hairPal.deep}"/>
    `;
  },

  renderEyes(hairPal: ReturnType<typeof palette5>, expression: CharacterExpression = 'normal'): string {
    // Normal eyes (default)
    const normalEyes = `
      <rect x="32" y="46" width="10" height="2" fill="#fff"/>
      <rect x="32" y="48" width="10" height="6" fill="#fff"/>
      <rect x="33" y="48" width="8" height="6" fill="${hairPal.base}"/>
      <rect x="34" y="49" width="6" height="4" fill="${hairPal.deep}"/>
      <rect x="35" y="50" width="4" height="3" fill="#000"/>
      <rect x="36" y="50" width="2" height="1" fill="#fff"/>
      <rect x="38" y="52" width="1" height="1" fill="#fff"/>
      <rect x="54" y="46" width="10" height="2" fill="#fff"/>
      <rect x="54" y="48" width="10" height="6" fill="#fff"/>
      <rect x="55" y="48" width="8" height="6" fill="${hairPal.base}"/>
      <rect x="56" y="49" width="6" height="4" fill="${hairPal.deep}"/>
      <rect x="57" y="50" width="4" height="3" fill="#000"/>
      <rect x="58" y="50" width="2" height="1" fill="#fff"/>
      <rect x="60" y="52" width="1" height="1" fill="#fff"/>
      <rect x="32" y="46" width="10" height="1" fill="${hairPal.deep}"/>
      <rect x="54" y="46" width="10" height="1" fill="${hairPal.deep}"/>
      <rect x="40" y="47" width="2" height="1" fill="${hairPal.deep}"/>
      <rect x="62" y="47" width="2" height="1" fill="${hairPal.deep}"/>
    `;

    if (expression === 'happy') {
      // ㅂ 자 눈 (happy squint arcs)
      return `
        <rect x="32" y="52" width="10" height="2" fill="${hairPal.deep}"/>
        <rect x="33" y="50" width="8"  height="2" fill="${hairPal.deep}"/>
        <rect x="34" y="49" width="6"  height="1" fill="${hairPal.deep}"/>
        <rect x="54" y="52" width="10" height="2" fill="${hairPal.deep}"/>
        <rect x="55" y="50" width="8"  height="2" fill="${hairPal.deep}"/>
        <rect x="56" y="49" width="6"  height="1" fill="${hairPal.deep}"/>
      `;
    }

    if (expression === 'wink') {
      // Left eye normal, right eye closed (line)
      return `
        <rect x="32" y="46" width="10" height="2" fill="#fff"/>
        <rect x="32" y="48" width="10" height="6" fill="#fff"/>
        <rect x="33" y="48" width="8" height="6" fill="${hairPal.base}"/>
        <rect x="34" y="49" width="6" height="4" fill="${hairPal.deep}"/>
        <rect x="35" y="50" width="4" height="3" fill="#000"/>
        <rect x="36" y="50" width="2" height="1" fill="#fff"/>
        <rect x="32" y="46" width="10" height="1" fill="${hairPal.deep}"/>
        <rect x="40" y="47" width="2" height="1" fill="${hairPal.deep}"/>
        <!-- right eye closed — just a line -->
        <rect x="54" y="51" width="10" height="2" fill="${hairPal.deep}"/>
        <rect x="54" y="46" width="10" height="1" fill="${hairPal.deep}"/>
        <rect x="62" y="47" width="2"  height="1" fill="${hairPal.deep}"/>
      `;
    }

    if (expression === 'surprised') {
      // Big round eyes
      return `
        <rect x="30" y="44" width="14" height="14" fill="#fff"/>
        <rect x="31" y="45" width="12" height="12" fill="#fff"/>
        <rect x="32" y="46" width="10" height="10" fill="${hairPal.base}"/>
        <rect x="33" y="47" width="8"  height="8"  fill="${hairPal.deep}"/>
        <rect x="34" y="48" width="6"  height="6"  fill="#000"/>
        <rect x="35" y="49" width="2"  height="2"  fill="#fff"/>
        <rect x="52" y="44" width="14" height="14" fill="#fff"/>
        <rect x="53" y="45" width="12" height="12" fill="#fff"/>
        <rect x="54" y="46" width="10" height="10" fill="${hairPal.base}"/>
        <rect x="55" y="47" width="8"  height="8"  fill="${hairPal.deep}"/>
        <rect x="56" y="48" width="6"  height="6"  fill="#000"/>
        <rect x="57" y="49" width="2"  height="2"  fill="#fff"/>
      `;
    }

    if (expression === 'blush') {
      // Normal eyes (blush handled in renderCheeks)
      return normalEyes;
    }

    return normalEyes;
  },

  renderCheeks(expression: CharacterExpression = 'normal'): string {
    const strongBlush = expression === 'blush' || expression === 'happy';
    const opacity = strongBlush ? '0.85' : '0.55';
    const opacity2 = strongBlush ? '0.7' : '0.4';
    return `
      <rect x="29" y="56" width="4" height="2" fill="${SKIN_CHEEK}" opacity="${opacity}"/>
      <rect x="63" y="56" width="4" height="2" fill="${SKIN_CHEEK}" opacity="${opacity}"/>
      <rect x="30" y="58" width="2" height="1" fill="${SKIN_CHEEK}" opacity="${opacity2}"/>
      <rect x="64" y="58" width="2" height="1" fill="${SKIN_CHEEK}" opacity="${opacity2}"/>
      ${strongBlush ? `
      <rect x="27" y="55" width="6" height="4" fill="${SKIN_CHEEK}" opacity="0.5"/>
      <rect x="63" y="55" width="6" height="4" fill="${SKIN_CHEEK}" opacity="0.5"/>
      ` : ''}
    `;
  },

  renderMouth(expression: CharacterExpression = 'normal'): string {
    if (expression === 'happy') {
      // Big wide smile
      return `
        <rect x="41" y="58" width="14" height="1" fill="#c14878"/>
        <rect x="40" y="59" width="16" height="3" fill="#e8568a"/>
        <rect x="41" y="59" width="14" height="1" fill="#ff7fa8"/>
        <rect x="41" y="60" width="4"  height="2" fill="#fff" opacity="0.5"/>
        <rect x="43" y="61" width="10" height="1" fill="#c14878" opacity="0.7"/>
      `;
    }
    if (expression === 'surprised') {
      // Round O mouth
      return `
        <rect x="44" y="57" width="8" height="1" fill="#c14878"/>
        <rect x="43" y="58" width="10" height="5" fill="#e8568a"/>
        <rect x="42" y="59" width="12" height="3" fill="#c14878"/>
        <rect x="44" y="59" width="8"  height="3" fill="#1a0010"/>
        <rect x="45" y="59" width="6"  height="1" fill="#ff7fa8" opacity="0.4"/>
      `;
    }
    if (expression === 'wink') {
      // Playful grin (wider one side)
      return `
        <rect x="43" y="58" width="10" height="1" fill="#c14878"/>
        <rect x="42" y="59" width="12" height="2" fill="#e8568a"/>
        <rect x="43" y="59" width="10" height="1" fill="#ff7fa8"/>
        <rect x="45" y="60" width="2"  height="1" fill="#fff" opacity="0.5"/>
        <rect x="53" y="60" width="2"  height="2" fill="#c14878"/>
      `;
    }
    // Normal / blush
    return `
      <rect x="44" y="58" width="8" height="1" fill="#c14878"/>
      <rect x="44" y="59" width="8" height="2" fill="#e8568a"/>
      <rect x="45" y="59" width="6" height="1" fill="#ff7fa8"/>
      <rect x="46" y="60" width="2" height="1" fill="#fff" opacity="0.5"/>
    `;
  },

  renderGlasses(color: string): string {
    return `
      <rect x="30" y="48" width="14" height="1" fill="${color}"/>
      <rect x="52" y="48" width="14" height="1" fill="${color}"/>
      <rect x="30" y="48" width="1" height="8" fill="${color}"/>
      <rect x="43" y="48" width="1" height="8" fill="${color}"/>
      <rect x="52" y="48" width="1" height="8" fill="${color}"/>
      <rect x="65" y="48" width="1" height="8" fill="${color}"/>
      <rect x="30" y="55" width="14" height="1" fill="${color}"/>
      <rect x="52" y="55" width="14" height="1" fill="${color}"/>
      <rect x="44" y="51" width="8" height="1" fill="${color}"/>
    `;
  },

  renderHairpin(color: string, iconStyle?: IconStyle): string {
    const c = palette5(color);
    const pink = palette5('#ffabc0');

    // 머리 위에 실제로 씌워지는 장식 — 종류별로 다른 모양
    switch (iconStyle) {
      case 'catEars':
        // 머리 양쪽 끝에 솟은 두 귀 — 안쪽 분홍
        return `
          <polygon points="30,32 36,18 42,32" fill="${c.shadow}"/>
          <polygon points="31,32 36,20 41,32" fill="${c.base}"/>
          <polygon points="34,30 36,24 38,30" fill="${pink.base}"/>
          <polygon points="54,32 60,18 66,32" fill="${c.shadow}"/>
          <polygon points="55,32 60,20 65,32" fill="${c.base}"/>
          <polygon points="58,30 60,24 62,30" fill="${pink.base}"/>
        `;
      case 'rabbitEars':
      case 'bunnyEars':
        // 위로 길게 뻗은 두 귀
        return `
          <rect x="34" y="6"  width="6" height="28" fill="${c.shadow}"/>
          <rect x="35" y="6"  width="4" height="28" fill="${c.base}"/>
          <rect x="36" y="10" width="2" height="20" fill="${pink.base}"/>
          <rect x="56" y="6"  width="6" height="28" fill="${c.shadow}"/>
          <rect x="57" y="6"  width="4" height="28" fill="${c.base}"/>
          <rect x="58" y="10" width="2" height="20" fill="${pink.base}"/>
        `;
      case 'bearEars':
        // 머리 위 양쪽에 둥근 귀
        return `
          <rect x="30" y="26" width="8" height="8" fill="${c.shadow}"/>
          <rect x="31" y="26" width="6" height="8" fill="${c.base}"/>
          <rect x="32" y="28" width="4" height="3" fill="${c.light}"/>
          <rect x="58" y="26" width="8" height="8" fill="${c.shadow}"/>
          <rect x="59" y="26" width="6" height="8" fill="${c.base}"/>
          <rect x="60" y="28" width="4" height="3" fill="${c.light}"/>
        `;
      case 'pandaEars':
        // 검정 둥근 귀 + 흰 안쪽
        return `
          <rect x="28" y="24" width="10" height="10" fill="#1a1a1a"/>
          <rect x="30" y="26" width="6"  height="6"  fill="#333"/>
          <rect x="58" y="24" width="10" height="10" fill="#1a1a1a"/>
          <rect x="60" y="26" width="6"  height="6"  fill="#333"/>
        `;
      case 'unicornHorn':
        // 머리 가운데 뿔
        return `
          <polygon points="44,30 48,8 52,30" fill="${c.shadow}"/>
          <polygon points="45,30 48,12 51,30" fill="${c.base}"/>
          <rect x="46" y="20" width="4" height="2" fill="${c.light}"/>
          <rect x="46" y="14" width="4" height="2" fill="${c.light}"/>
        `;
      case 'wizardHat':
        // 길쭉한 마법사 모자
        return `
          <polygon points="30,34 48,2 66,34" fill="${c.shadow}"/>
          <polygon points="32,34 48,6 64,34" fill="${c.base}"/>
          <rect x="26" y="32" width="44" height="4" fill="${c.shadow}"/>
          <rect x="26" y="32" width="44" height="2" fill="${c.deep}"/>
          <rect x="42" y="14" width="6" height="2" fill="${c.light}"/>
        `;
      case 'crown':
      case 'bigCrown':
        // 왕관 — 위 지그재그
        return `
          <polygon points="28,34 32,18 38,28 44,14 52,14 58,28 64,18 68,34" fill="${c.shadow}"/>
          <polygon points="30,34 32,22 38,30 44,18 52,18 58,30 64,22 66,34" fill="${c.base}"/>
          <rect x="28" y="32" width="40" height="4" fill="${c.deep}"/>
          <rect x="34" y="22" width="2" height="2" fill="#fff"/>
          <rect x="46" y="20" width="2" height="2" fill="#fff"/>
          <rect x="58" y="22" width="2" height="2" fill="#fff"/>
        `;
      case 'tiara':
        // 작은 티아라
        return `
          <polygon points="34,34 38,24 44,30 48,20 52,30 58,24 62,34" fill="${c.shadow}"/>
          <polygon points="36,34 38,28 44,32 48,24 52,32 58,28 60,34" fill="${c.base}"/>
          <rect x="34" y="32" width="28" height="3" fill="${c.deep}"/>
          <rect x="47" y="26" width="2" height="2" fill="#fff"/>
        `;
      case 'halo':
        // 머리 위 빛나는 고리
        return `
          <ellipse cx="48" cy="14" rx="20" ry="4" fill="none" stroke="${c.shadow}" stroke-width="3"/>
          <ellipse cx="48" cy="14" rx="20" ry="4" fill="none" stroke="${c.base}" stroke-width="2"/>
          <ellipse cx="48" cy="14" rx="20" ry="4" fill="none" stroke="${c.light}" stroke-width="1"/>
        `;
      case 'devilHorns':
        // 작은 뿔 두 개
        return `
          <polygon points="32,34 30,22 36,28" fill="${c.shadow}"/>
          <polygon points="33,34 31,24 35,30" fill="${c.base}"/>
          <polygon points="64,34 66,22 60,28" fill="${c.shadow}"/>
          <polygon points="63,34 65,24 61,30" fill="${c.base}"/>
        `;
      case 'cap':
        // 야구모자 (앞 챙)
        return `
          <rect x="28" y="28" width="40" height="8" fill="${c.shadow}"/>
          <rect x="28" y="28" width="40" height="6" fill="${c.base}"/>
          <rect x="28" y="28" width="40" height="2" fill="${c.light}"/>
          <rect x="22" y="34" width="22" height="4" fill="${c.deep}"/>
          <rect x="44" y="32" width="6"  height="2" fill="${c.deep}"/>
        `;
      case 'beanie':
        // 비니 (머리 덮는 둥근 모자)
        return `
          <rect x="26" y="22" width="44" height="14" fill="${c.shadow}"/>
          <rect x="28" y="22" width="40" height="12" fill="${c.base}"/>
          <rect x="28" y="22" width="40" height="2"  fill="${c.light}"/>
          <rect x="26" y="32" width="44" height="3"  fill="${c.deep}"/>
          <rect x="44" y="18" width="8"  height="6"  fill="${c.shadow}"/>
          <rect x="45" y="18" width="6"  height="6"  fill="${c.base}"/>
        `;
      case 'beretCap':
        // 베레모 (살짝 기울어진 둥근 모자)
        return `
          <rect x="26" y="26" width="40" height="10" fill="${c.shadow}"/>
          <rect x="28" y="24" width="36" height="10" fill="${c.base}"/>
          <rect x="28" y="24" width="36" height="2"  fill="${c.light}"/>
          <rect x="56" y="22" width="6"  height="4"  fill="${c.deep}"/>
        `;
      case 'strawHat':
        // 밀짚 모자 — 넓은 챙
        return `
          <rect x="14" y="32" width="68" height="4" fill="${c.shadow}"/>
          <rect x="14" y="32" width="68" height="2" fill="${c.base}"/>
          <rect x="28" y="22" width="40" height="12" fill="${c.shadow}"/>
          <rect x="30" y="22" width="36" height="10" fill="${c.base}"/>
          <rect x="30" y="22" width="36" height="2"  fill="${c.light}"/>
          <rect x="30" y="28" width="36" height="2"  fill="${c.deep}"/>
        `;
      case 'bigRibbon':
        // 머리 위 큰 리본
        return `
          <polygon points="26,30 36,22 36,38" fill="${c.shadow}"/>
          <polygon points="28,30 36,24 36,36" fill="${c.base}"/>
          <polygon points="70,30 60,22 60,38" fill="${c.shadow}"/>
          <polygon points="68,30 60,24 60,36" fill="${c.base}"/>
          <rect x="42" y="26" width="12" height="10" fill="${c.shadow}"/>
          <rect x="43" y="27" width="10" height="8"  fill="${c.base}"/>
          <rect x="46" y="28" width="4"  height="6"  fill="${c.deep}"/>
        `;
      case 'starPin':
        // 머리 위 한쪽에 별
        return `
          <polygon points="38,30 40,24 42,30 48,30 43,33 45,38 40,35 35,38 37,33 32,30" fill="${c.shadow}"/>
          <polygon points="38,30 40,25 42,30 47,30 43,33 45,37 40,34 35,37 37,33 33,30" fill="${c.base}"/>
        `;
      case 'heartPin':
        // 머리 위 하트
        return `
          <rect x="36" y="26" width="4" height="6" fill="${c.shadow}"/>
          <rect x="40" y="26" width="4" height="6" fill="${c.shadow}"/>
          <rect x="34" y="28" width="12" height="4" fill="${c.shadow}"/>
          <rect x="36" y="32" width="8"  height="2" fill="${c.shadow}"/>
          <rect x="38" y="34" width="4"  height="2" fill="${c.shadow}"/>
          <rect x="37" y="27" width="2" height="4" fill="${c.base}"/>
          <rect x="41" y="27" width="2" height="4" fill="${c.base}"/>
          <rect x="35" y="29" width="10" height="3" fill="${c.base}"/>
          <rect x="37" y="32" width="6"  height="2" fill="${c.base}"/>
        `;
      case 'flowerPin':
      case 'sakuraPin':
        // 머리 위 꽃
        return `
          <rect x="34" y="24" width="6" height="6" fill="${c.shadow}"/>
          <rect x="44" y="24" width="6" height="6" fill="${c.shadow}"/>
          <rect x="34" y="32" width="6" height="6" fill="${c.shadow}"/>
          <rect x="44" y="32" width="6" height="6" fill="${c.shadow}"/>
          <rect x="35" y="25" width="4" height="4" fill="${c.base}"/>
          <rect x="45" y="25" width="4" height="4" fill="${c.base}"/>
          <rect x="35" y="33" width="4" height="4" fill="${c.base}"/>
          <rect x="45" y="33" width="4" height="4" fill="${c.base}"/>
          <rect x="40" y="29" width="4" height="4" fill="#ffd95c"/>
        `;
      case 'butterflyPin':
        return `
          <ellipse cx="38" cy="28" rx="4" ry="5" fill="${c.shadow}"/>
          <ellipse cx="38" cy="28" rx="3" ry="4" fill="${c.base}"/>
          <ellipse cx="46" cy="28" rx="4" ry="5" fill="${c.shadow}"/>
          <ellipse cx="46" cy="28" rx="3" ry="4" fill="${c.base}"/>
          <rect x="41" y="24" width="2" height="10" fill="${c.deep}"/>
        `;
      case 'leaf':
        return `
          <polygon points="40,34 44,18 50,30" fill="${c.shadow}"/>
          <polygon points="41,33 44,20 49,30" fill="${c.base}"/>
        `;
      case 'headband':
      case 'ribbon':
      default:
        // 기본 머리띠 + 작은 리본
        return `
          <rect x="26" y="34" width="44" height="4" fill="${c.deep}"/>
          <rect x="26" y="34" width="44" height="1" fill="${c.rim}"/>
          <rect x="27" y="35" width="42" height="2" fill="${c.base}"/>
          <polygon points="40,32 36,28 40,36" fill="${c.shadow}"/>
          <polygon points="40,32 36,29 40,35" fill="${c.base}"/>
          <polygon points="48,32 52,28 48,36" fill="${c.shadow}"/>
          <polygon points="48,32 52,29 48,35" fill="${c.base}"/>
          <rect x="42" y="30" width="4" height="6" fill="${c.deep}"/>
        `;
    }
  },

  renderNeck(): string {
    return `
      <rect x="42" y="66" width="12" height="4" fill="${SKIN.base}"/>
      <rect x="42" y="66" width="12" height="2" fill="${SKIN.shadow}"/>
      <rect x="42" y="66" width="2" height="4" fill="${SKIN.deep}"/>
    `;
  },

  renderTop(top: ReturnType<typeof palette5>): string {
    return `
      <rect x="28" y="70" width="40" height="3" fill="${top.deep}"/>
      <rect x="26" y="73" width="44" height="28" fill="${top.base}"/>
      <rect x="26" y="73" width="3" height="28" fill="${top.shadow}"/>
      <rect x="67" y="73" width="3" height="28" fill="${top.shadow}"/>
      <rect x="26" y="73" width="2" height="6" fill="${top.deep}"/>
      <rect x="68" y="73" width="2" height="6" fill="${top.deep}"/>
      <rect x="29" y="73" width="38" height="2" fill="${top.light}"/>
      <rect x="32" y="76" width="2" height="22" fill="${top.light}" opacity="0.4"/>
      <rect x="47" y="76" width="2" height="2" fill="${top.deep}"/>
      <rect x="47" y="82" width="2" height="2" fill="${top.deep}"/>
      <rect x="47" y="88" width="2" height="2" fill="${top.deep}"/>
      <rect x="47" y="94" width="2" height="2" fill="${top.deep}"/>
      <rect x="26" y="99" width="44" height="2" fill="${top.deep}"/>
    `;
  },

  renderArms(top: ReturnType<typeof palette5>, armOffsets: [number, number] = [0, 0]): string {
    const [lo, ro] = armOffsets; // left offset, right offset (y)
    return `
      <rect x="20" y="${70 + lo}" width="8" height="3" fill="${top.deep}"/>
      <rect x="18" y="${73 + lo}" width="8" height="22" fill="${top.shadow}"/>
      <rect x="20" y="${73 + lo}" width="6" height="22" fill="${top.base}"/>
      <rect x="20" y="${73 + lo}" width="6" height="1" fill="${top.light}"/>
      <rect x="18" y="${95 + lo}" width="8" height="6" fill="${SKIN.shadow}"/>
      <rect x="20" y="${95 + lo}" width="6" height="6" fill="${SKIN.base}"/>
      <rect x="20" y="${95 + lo}" width="6" height="1" fill="${SKIN.light}"/>
      <rect x="68" y="${70 + ro}" width="8" height="3" fill="${top.deep}"/>
      <rect x="70" y="${73 + ro}" width="8" height="22" fill="${top.shadow}"/>
      <rect x="70" y="${73 + ro}" width="6" height="22" fill="${top.base}"/>
      <rect x="74" y="${73 + ro}" width="2" height="22" fill="${top.shadow}"/>
      <rect x="70" y="${95 + ro}" width="8" height="6" fill="${SKIN.shadow}"/>
      <rect x="70" y="${95 + ro}" width="6" height="6" fill="${SKIN.base}"/>
      <rect x="70" y="${95 + ro}" width="6" height="1" fill="${SKIN.light}"/>
    `;
  },

  renderBottom(bottom: ReturnType<typeof palette5>): string {
    return `
      <rect x="26" y="101" width="44" height="3" fill="${bottom.deep}"/>
      <rect x="26" y="104" width="44" height="22" fill="${bottom.base}"/>
      <rect x="26" y="104" width="44" height="1" fill="${bottom.light}"/>
      <rect x="26" y="104" width="3" height="22" fill="${bottom.shadow}"/>
      <rect x="67" y="104" width="3" height="22" fill="${bottom.shadow}"/>
      <rect x="46" y="104" width="4" height="22" fill="${bottom.deep}"/>
      <rect x="47" y="104" width="2" height="22" fill="${bottom.shadow}"/>
    `;
  },

  renderLegs(legOffsets: [number, number] = [0, 0]): string {
    const [lo, ro] = legOffsets; // left leg y-offset, right leg y-offset
    return `
      <rect x="28" y="${126 + lo}" width="14" height="10" fill="${SKIN.shadow}"/>
      <rect x="30" y="${126 + lo}" width="12" height="10" fill="${SKIN.base}"/>
      <rect x="30" y="${126 + lo}" width="2" height="10" fill="${SKIN.deep}"/>
      <rect x="54" y="${126 + ro}" width="14" height="10" fill="${SKIN.shadow}"/>
      <rect x="54" y="${126 + ro}" width="12" height="10" fill="${SKIN.base}"/>
      <rect x="54" y="${126 + ro}" width="2" height="10" fill="${SKIN.deep}"/>
    `;
  },

  renderShoes(shoes: ReturnType<typeof palette5>, legOffsets: [number, number] = [0, 0]): string {
    const [lo, ro] = legOffsets;
    return `
      <rect x="26" y="${136 + lo}" width="18" height="8" fill="${shoes.shadow}"/>
      <rect x="26" y="${136 + lo}" width="18" height="6" fill="${shoes.base}"/>
      <rect x="26" y="${136 + lo}" width="18" height="1" fill="${shoes.light}"/>
      <rect x="28" y="${138 + lo}" width="4" height="1" fill="${shoes.rim}" opacity="0.7"/>
      <rect x="26" y="${142 + lo}" width="18" height="2" fill="${shoes.deep}"/>
      <rect x="32" y="${138 + lo}" width="6" height="1" fill="${shoes.deep}"/>
      <rect x="32" y="${140 + lo}" width="6" height="1" fill="${shoes.deep}"/>
      <rect x="52" y="${136 + ro}" width="18" height="8" fill="${shoes.shadow}"/>
      <rect x="52" y="${136 + ro}" width="18" height="6" fill="${shoes.base}"/>
      <rect x="52" y="${136 + ro}" width="18" height="1" fill="${shoes.light}"/>
      <rect x="54" y="${138 + ro}" width="4" height="1" fill="${shoes.rim}" opacity="0.7"/>
      <rect x="52" y="${142 + ro}" width="18" height="2" fill="${shoes.deep}"/>
      <rect x="58" y="${138 + ro}" width="6" height="1" fill="${shoes.deep}"/>
      <rect x="58" y="${140 + ro}" width="6" height="1" fill="${shoes.deep}"/>
    `;
  },

  /**
   * GameState의 currentEquipped(string ID 맵)을 EquippedMap으로 변환
   */
  resolveEquipped(equipped: Partial<Record<OutfitSlot, string>>, outfitsById: Record<string, Outfit>): EquippedMap {
    const result: EquippedMap = {};
    for (const slot of Object.keys(equipped) as OutfitSlot[]) {
      const id = equipped[slot];
      if (!id) continue;
      const o = outfitsById[id];
      if (o) result[slot] = this.toEquipped(o);
    }
    return result;
  },
};
